"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/Button";
import FilterButtons from "../../../components/universal-filter-tabs.jsx";
import CustomerDetails from "../../global/view/customer-detailsForm.jsx";
import { useAuth } from "../../../context/AuthContext";
import RpDebtors from "../rpdebtors.jsx";
import styles from "../css/creditDebtorsList.module.css";
import api from "../../../api";

const CreditDebtorsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = Number(user?.roleid);

  const isRpRole = roleId === 3 || roleId === 8;

  const [activeStatus, setActiveStatus] = useState("All");
  const [rows, setRows] = useState([]);

  const handleView = async (row) => {
    try {
      const applicationId = row?.applicationId;
      const res = await api.get(`/api/public/credit-applications/${applicationId}`);
      const application = res?.data?.application;
      if (!application) {
        alert("Unable to load application details");
        return;
      }

      const normalizedDob = application.dob
        ? new Date(application.dob).toISOString().slice(0, 10)
        : "";

      const legacyAddress = String(application.address || "");
      const parts = legacyAddress.split(",").map((p) => p.trim()).filter(Boolean);
      const streetAddress = application.streetAddress || parts[0] || legacyAddress;
      const suburb = application.suburb || parts[1] || "";
      const city = application.city || parts[2] || "";
      const postal = String(application.postalCode || parts[3] || "")
        .replace(/\D/g, "")
        .slice(0, 4);

      navigate("/credits", {
        state: {
          creditFlow: {
            mode: "review",
            status: String(application.status || "").toLowerCase(),
            applicationId: application.applicationId,
            applicant: {
              fullName: `${application.firstName || ""} ${application.lastName || ""}`.trim(),
              idNumber: application.idNumber || "",
            },
          },
          formData: {
            firstName: application.firstName || "",
            lastName: application.lastName || "",
            idNumber: application.idNumber || "",
            dob: normalizedDob,
            gender: application.gender || "",
            // In review mode we keep file objects null, but we can still display stored URLs if the UI supports it
            idCopy: null,
            idCopyUrl: application.idCopyUrl || "",
            mobile: String(application.phone || ""),
            email: application.email || "",
            streetAddress,
            suburb,
            city,
            postalCode: postal,
            province: application.province || "",
            employmentStatus: application.employmentStatus || "",
            employerName: application.employerName || "",
            monthlyIncome: application.monthlyIncome || "",
            payFrequency: application.payFrequency || "",
            payslip: null,
            payslipUrl: application.payslipUrl || "",
            bankStatement: null,
            bankStatementUrl: application.bankStatementUrl || "",
            hasExistingCredit: Boolean(application.hasExistingCredit),
            preferredPaymentMethod: application.preferredPaymentMethod || "",
            expenses: Array.isArray(application.expenses) ? application.expenses : [],
          },
        },
      });
    } catch (err) {
      console.error("Failed to load application:", err);
      alert(err?.message || "Failed to load application");
    }
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const res = await api.get("/api/public/credit-applications", {
          params: {
            status: "awaiting,decision,pending",
          },
        });
        const payload = res?.data;
        const nextRows = Array.isArray(payload?.rows) ? payload.rows : [];
        if (mounted) setRows(nextRows);
      } catch (err) {
        console.error("Failed to load credit applications:", err);
        if (mounted) setRows([]);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const columns = useMemo(
    () => [
      { header: "ID Number", accessor: "idNumber" },
      { header: "Full Name", accessor: "fullName" },
      {
        header: "Status",
        accessor: "status",
        render: (_val, row) => {
          const normalized = String(row?.status || "").toLowerCase();
          const badgeClass =
            normalized === "awaiting"
              ? styles.statusAwaiting
              : normalized === "decision"
                ? styles.statusDecision
                : styles.statusPending;

          return <span className={`${styles.statusPill} ${badgeClass}`}>{row.status}</span>;
        },
      },
      {
        header: "Action",
        accessor: "action",
        render: (_val, row) => (
          <Button
            variant="primary"
            type="button"
            className={styles.viewBtn}
            onClick={() => {
              handleView(row);
            }}
          >
            View
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const headerContent = (
    <FilterButtons
      buttons={["All", "Awaiting", "Decision", "Pending"]}
      activeButton={activeStatus}
      onButtonClick={(label) => setActiveStatus(label)}
    />
  );

  const fetchRows = async (searchTerm) => {
    const term = String(searchTerm || "").trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((r) => {
      const name = String(r.fullName || "").toLowerCase();
      const id = String(r.idNumber || "").toLowerCase();
      return name.includes(term) || id.includes(term);
    });
  };

  const filterProfiles = (row) => {
    if (String(activeStatus).toLowerCase() === "all") return true;
    return String(row?.status || "").toLowerCase() === String(activeStatus || "").toLowerCase();
  };

  if (isRpRole) {
    return <RpDebtors />;
  }

  return (
    <div className={styles.page}>
      <CustomerDetails
        backTo="/dashboard"
        showTitle
        titleText="Credit Applications"
        headerContent={headerContent}
        actionButtonLabel="View"
        fetchRows={fetchRows}
        filterProfiles={filterProfiles}
        columns={columns}
        searchPlaceholder="Search Name"
        searchLabelText="Filter"
        loadingText="Loading applications..."
        emptyText="No applications found."
      />
    </div>
  );
};

export default CreditDebtorsList;
